import fuzzy from 'fuzzy';
import React, { useCallback, useMemo } from 'react';
import { sortBy } from 'remeda';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import {
  ModalContainer,
  ModalList,
  type ModalListFilter,
} from 'src/components/ModalContainer';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { QuickAction } from './quick-actions-context';

export type QuickActionsModalProps = {
  isDisabled?: boolean;
  actions: QuickAction[];
};

const UNGROUPED_ACTIONS_GROUP = 'Actions';

const fuzzyKeywordFilter: ModalListFilter = (_, search, keywords = []) =>
  fuzzy.filter(search, keywords).length > 0 ? 1 : 0;

export const QuickActionsModal: React.FC = () => {
  const { isOpened, modalProps } = useModalState(Modals.QuickActionsModal);
  const { closeModal } = useModalMethods(Modals.QuickActionsModal);
  const { showModal: showKeyboardShortcutsModal } = useModalMethods(Modals.ShortcutsModal);
  const { showModal: showContactSupportModal } = useModalMethods(Modals.ContactSupportModal);

  const { actions } = modalProps!;

  const onItemSelect = useCallback(
    (item: QuickAction) => {
      closeModal();
      item.handler?.();
    },
    [closeModal],
  );

  const openKeyboardShortcutsModal = useCallback(() => {
    showKeyboardShortcutsModal();
  }, [showKeyboardShortcutsModal]);

  const openContactSupportModal = useCallback(() => {
    showContactSupportModal();
  }, [showContactSupportModal]);

  const helpAndSupportActions = useMemo<QuickAction[]>(
    () => [
      {
        id: 'keyboard-shortcuts',
        title: 'Keyboard Shortcuts',
        description: 'Browse the list of available keyboard shortcuts.',
        icon: <DynamicIcon name="keyboard" library="lucide" />,
        handler: openKeyboardShortcutsModal,
        keywords: [
          'Keyboard Shortcuts',
          'Browse the list of available keyboard shortcuts.',
          'Help & Support',
        ],
        group: 'Help & Support',
        order: Number.MAX_SAFE_INTEGER,
      },
      {
        id: 'contact-support',
        title: 'Contact Support',
        description: 'Reach out to our support team for additional help.',
        icon: <DynamicIcon name="headphones" library="lucide" />,
        handler: openContactSupportModal,
        keywords: [
          'Contact Support',
          'Reach out to our support team for additional help.',
          'Help & Support',
        ],
        group: 'Help & Support',
        order: Number.MAX_SAFE_INTEGER,
      },
    ],
    [openKeyboardShortcutsModal, openContactSupportModal],
  );

  const orderedActions = useMemo(
    () =>
      sortBy([...actions, ...helpAndSupportActions], (action) => action.order ?? 999).map(
        (action) => ({ ...action, group: action.group ?? UNGROUPED_ACTIONS_GROUP }),
      ),
    [actions, helpAndSupportActions],
  );

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Quick actions"
      onClose={closeModal}
      data-testid="quick-actions-modal"
    >
      <ModalList
        items={orderedActions}
        onSelect={onItemSelect}
        filter={fuzzyKeywordFilter}
        testId="quick-actions-modal"
      />
    </ModalContainer>
  );
};
